import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { Row, Col, Card, Image } from "antd";
import { Form, Input, Button } from "antd";

import type { SettingsResponse } from "@chirpstack/chirpstack-api-grpc-web/api/internal_pb";
import { OpenIdConnectLoginRequest, OAuth2LoginRequest } from "@chirpstack/chirpstack-api-grpc-web/api/internal_pb";

import SessionStore from "../../stores/SessionStore";
import InternalStore from "../../stores/InternalStore";
import { useTitle } from "../helpers";

interface LoginFormValues {
  email: string;
  password: string;
}

interface OidcLoginProps {
  loginUrl: string;
  loginLabel: string;
}

interface OAuth2LoginProps {
  loginUrl: string;
  loginLabel: string;
}

function OidcLogin({ loginUrl, loginLabel }: OidcLoginProps) {
  return (
    <Row justify="center" style={{ marginTop: "100px", padding: "0 16px" }}>
      <Col xs={22} sm={18} md={12} lg={8} xl={6}>
        <Card 
          title={
            <div style={{ textAlign: "center" }}>
              <Image 
                src="/bmpLogo.png" 
                alt="BMP Logic Logo" 
                preview={false}
                style={{ height: "60px", marginTop: "24px", marginBottom: "16px" }}
              />
              <div style={{ fontSize: "18px", fontWeight: "600", color: "#1890ff" }}>
                BMP Logic agriStack
              </div>
            </div>
          }
          style={{ boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}
        >
          <div style={{ textAlign: "center" }}>
            <a href={loginUrl}>
              <Button type="primary" size="large" style={{ width: "100%" }}>
                {loginLabel}
              </Button>
            </a>
          </div>
        </Card>
      </Col>
    </Row>
  );
}

function OAuth2Login({ loginUrl, loginLabel }: OAuth2LoginProps) {
  return (
    <Row justify="center" style={{ marginTop: "100px", padding: "0 16px" }}>
      <Col xs={22} sm={18} md={12} lg={8} xl={6}>
        <Card 
          title={
            <div style={{ textAlign: "center" }}>
              <Image 
                src="/bmpLogo.png" 
                alt="BMP Logic Logo" 
                preview={false}
                style={{ height: "60px", marginTop: "24px", marginBottom: "16px" }}
              />
              <div style={{ fontSize: "18px", fontWeight: "600", color: "#1890ff" }}>
                BMP Logic agriStack
              </div>
            </div>
          }
          style={{ boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}
        >
          <div style={{ textAlign: "center" }}>
            <a href={loginUrl}>
              <Button type="primary" size="large" style={{ width: "100%" }}>
                {loginLabel}
              </Button>
            </a>
          </div>
        </Card>
      </Col>
    </Row>
  );
}

function LoginForm() {
  const navigate = useNavigate();

  const onFinish = (values: LoginFormValues) => {
    SessionStore.login(values.email, values.password, () => {
      navigate("/");
    });
  };

  return (
    <Row justify="center" style={{ marginTop: "100px", padding: "0 16px" }}>
      <Col xs={22} sm={18} md={12} lg={8} xl={6}>
        <Card 
          title={
            <div style={{ textAlign: "center" }}>
              <Image 
                src="/bmpLogo.png" 
                alt="BMP Logic Logo" 
                preview={false}
                style={{ height: "60px", marginTop: "24px", marginBottom: "16px" }}
              />
              <div style={{ fontSize: "18px", fontWeight: "600", color: "#1890ff" }}>
                Login to BMP Logic agriStack Dashboard
              </div>
            </div>
          }
          style={{ boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}
        >
          <Form 
            layout="vertical" 
            onFinish={onFinish}
            size="large"
          >
            <Form.Item
              label="Username / Email"
              name="email"
              rules={[
                {
                  required: true,
                  message: "Please enter your email / username!",
                },
              ]}
            >
              <Input placeholder="Enter your username or email" />
            </Form.Item>

            <Form.Item
              label="Password"
              name="password"
              rules={[
                {
                  required: true,
                  message: "Please enter your password!",
                },
              ]}
            >
              <Input.Password placeholder="Enter your password" />
            </Form.Item>

            <Form.Item style={{ marginBottom: 0 }}>
              <Button type="primary" htmlType="submit" size="large" style={{ width: "100%" }}>
                Login
              </Button>
            </Form.Item>
          </Form>
        </Card>
      </Col>
    </Row>
  );
}

function Login() {
  useTitle("Login");
  const location = useLocation();
  const navigate = useNavigate();

  const [loaded, setLoaded] = useState<boolean>(false);
  const [oidcEnabled, setOidcEnabled] = useState<boolean>(false);
  const [oAuth2Enabled, setOAuth2Enabled] = useState<boolean>(false);
  const [oidcLoginLabel, setOidcLoginLabel] = useState<string>("");
  const [oidcLoginUrl, setOidcLoginUrl] = useState<string>("");
  const [oAuth2LoginLabel, setOAuth2LoginLabel] = useState<string>("");
  const [oAuth2LoginUrl, setOAuth2LoginUrl] = useState<string>("");

  useEffect(() => {
    SessionStore.logout(true, () => {});

    InternalStore.settings((resp: SettingsResponse) => {
      const oidc = resp.getOpenidConnect()!;
      const oAuth2 = resp.getOauth2()!;

      setOidcEnabled(oidc.getEnabled());
      setOidcLoginLabel(oidc.getLoginLabel());
      setOidcLoginUrl(oidc.getLoginUrl());

      setOAuth2Enabled(oAuth2.getEnabled());
      setOAuth2LoginLabel(oAuth2.getLoginLabel());
      setOAuth2LoginUrl(oAuth2.getLoginUrl());

      if (location.search !== "") {
        // Callback from OIDC or OAuth2 provider.
        const q = new URLSearchParams(location.search);

        if (oidc.getEnabled()) {
          const req = new OpenIdConnectLoginRequest();
          req.setCode(q.get("code") || "");
          req.setState(q.get("state") || "");

          SessionStore.openIdConnectLogin(req, () => {
            navigate("/");
          });
        } else if (oAuth2.getEnabled()) {
          const req = new OAuth2LoginRequest();
          req.setCode(q.get("code") || "");
          req.setState(q.get("state") || "");

          SessionStore.oAuth2Login(req, () => {
            navigate("/");
          });
        }
      } else {
        if (oidc.getEnabled() && oidc.getLoginRedirect()) {
          window.location.replace(oidc.getLoginUrl());
        } else if (oAuth2.getEnabled() && oAuth2.getLoginRedirect()) {
          window.location.replace(oAuth2.getLoginUrl());
        } else {
          setLoaded(true);
        }
      }
    });
  }, [location, navigate]);

  if (!loaded) {
    return null;
  }

  if (oidcEnabled) {
    return <OidcLogin loginUrl={oidcLoginUrl} loginLabel={oidcLoginLabel} />;
  } else if (oAuth2Enabled) {
    return <OAuth2Login loginUrl={oAuth2LoginUrl} loginLabel={oAuth2LoginLabel} />;
  } else {
    return <LoginForm />;
  }
}

export default Login;
